<script>
export default {
    props: {
        title: {
            type: String,
            default: null
        },
        columns: {
            type: Array,
            required: true
        },
        rows: {
            type: Array,
            required: true
        },
        evaluatedStatuses: {
            type: Object,
            default: () => ({})
        },
        links: {
            type: Object,
            default: () => ({})
        },
        sortKeys: {
            type: Object,
            default: () => ({})
        },
        defaultSort: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            sortColumn: this.defaultSort ? this.defaultSort.column : null,
            sortDirection: this.defaultSort?.direction === 'desc' ? 'desc' : 'asc'
        };
    },

    computed: {
        // Rows are paired with the index they arrived in so that statuses and links,
        // which the backend keys by the original row index, survive sorting.
        indexedRows() {
            return this.rows.map((row, index) => ({ row, index }));
        },

        sortedRows() {
            if (this.sortColumn === null) {
                return this.indexedRows;
            }

            return [...this.indexedRows].sort((a, b) => {
                const comparison = this.compareValues(
                    this.getSortValue(a.index, this.sortColumn),
                    this.getSortValue(b.index, this.sortColumn)
                );

                return this.sortDirection === 'desc' ? -comparison : comparison;
            });
        }
    },

    methods: {
        getSortValue(rowIndex, cellIndex) {
            const sortKey = this.sortKeys[String(rowIndex)]?.[String(cellIndex)]
                ?? this.sortKeys[rowIndex]?.[cellIndex];

            return sortKey ?? this.rows[rowIndex]?.[cellIndex] ?? '';
        },

        compareValues(a, b) {
            const numberA = this.toNumber(a);
            const numberB = this.toNumber(b);

            if (numberA !== null && numberB !== null) {
                return numberA - numberB;
            }

            return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        },

        toNumber(value) {
            if (typeof value === 'number') {
                return Number.isFinite(value) ? value : null;
            }

            if (typeof value !== 'string' || value.trim() === '') {
                return null;
            }

            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : null;
        },

        toggleSort(cellIndex) {
            if (this.sortColumn === cellIndex) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
                return;
            }

            this.sortColumn = cellIndex;
            this.sortDirection = 'asc';
        },

        sortIcon(cellIndex) {
            if (this.sortColumn !== cellIndex) {
                return 'icon-sort-amount-asc opacity-0 group-hover:opacity-40';
            }

            return this.sortDirection === 'asc' ? 'icon-sort-amount-asc' : 'icon-sort-amount-desc';
        },

        ariaSort(cellIndex) {
            if (this.sortColumn !== cellIndex) {
                return 'none';
            }

            return this.sortDirection === 'asc' ? 'ascending' : 'descending';
        },

        getCellStatusClass(rowIndex, cellIndex) {
            const status = this.evaluatedStatuses[String(rowIndex)]?.[String(cellIndex)]
                || this.evaluatedStatuses[rowIndex]?.[cellIndex]
                || 'normal';

            return {
                'normal': 'text-gray-900',
                'warning': 'text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded inline-block',
                'danger': 'text-red-700 bg-red-50 px-2 py-0.5 rounded inline-block'
            }[status] || 'text-gray-900';
        },

        hasStatus(rowIndex, cellIndex) {
            const status = this.evaluatedStatuses[String(rowIndex)]?.[String(cellIndex)]
                || this.evaluatedStatuses[rowIndex]?.[cellIndex];
            return status && status !== 'normal';
        },

        getCellLink(rowIndex, cellIndex) {
            return this.links[String(rowIndex)]?.[String(cellIndex)]
                || this.links[rowIndex]?.[cellIndex]
                || null;
        },

        hasLink(rowIndex, cellIndex) {
            return this.getCellLink(rowIndex, cellIndex) !== null;
        }
    }
}
</script>

<template>
    <div class="overflow-x-auto">
        <h4 v-if="title" class="text-xs font-medium text-gray-700 uppercase tracking-wide p-0 m-0 mb-2">
            {{ title }}
        </h4>
        <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
            <tr>
                <th v-for="(col, colIdx) in columns" :key="col"
                    :aria-sort="ariaSort(colIdx)"
                    class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button type="button"
                            @click="toggleSort(colIdx)"
                            :title="`Sort by ${col}`"
                            class="group bg-transparent border-0 p-0 m-0 inline-flex items-center gap-1 font-medium text-xs text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 focus:text-gray-700">
                        {{ col }}
                        <span :class="['lnr', '!text-xs', sortIcon(colIdx)]"></span>
                    </button>
                </th>
            </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="{ row, index: rowIdx } in sortedRows" :key="rowIdx">
                <td v-for="(cell, cellIdx) in row" :key="cellIdx"
                    class="px-4 py-2 text-sm">

                    <!-- Cell with link -->
                    <a v-if="hasLink(rowIdx, cellIdx)"
                       :href="getCellLink(rowIdx, cellIdx)"
                       target="_blank"
                       :class="['hover:text-blue-500 hover:no-underline focus:text-blue-600 focus:no-underline', getCellStatusClass(rowIdx, cellIdx)]">
                        {{ cell }}
                        <span class="lnr icon-link !text-blue-500 !text-xs ml-1 hover:no-underline"></span>
                    </a>

                    <!-- Cell with status but no link -->
                    <span v-else-if="hasStatus(rowIdx, cellIdx)"
                          :class="getCellStatusClass(rowIdx, cellIdx)">
                        {{ cell }}
                    </span>

                    <!-- Regular cell -->
                    <template v-else>
                        <span class="text-gray-900">{{ cell }}</span>
                    </template>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>
